import React, { useEffect, useMemo, useState } from "react";
import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfToday,
  startOfWeek,
  subDays,
  eachDayOfInterval,
  isWeekend,
  isFuture,
  isToday,
} from "date-fns";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";

const TYPES = {
  VACATION: "vacation",
  FLEX: "flex",
  SICK: "sick",
  WFH: "wfh",
  MEETING_WITH_CLIENT: "meeting_with_client",
  EDUCATION: "education",
  FULL_DAY: "full_day",
  INDUSTRY_EVENT: "industry_event",
  WEEKLY_DAY_OFF: "weekly_day_off",
  OFFICE_CLOSED: "office_closed",
  CLOSING_EARLY: "closing_early",
};

const COUNTABLE_VACATION_MIN_HOURS = 2;
const VACATION_EXCEPTION_CONTACT = "benjamin@mcins.ca";

const toEmailLower = (email) => String(email || "").trim().toLowerCase();

const ESTHER_EMAIL = "esther@mcins.ca";
const ESTHER_EMAIL_LOWER = toEmailLower(ESTHER_EMAIL);
const ESTHER_DEFAULT_NAME = "Esther Mandel";

function ymd(dateObj) {
  return format(dateObj, "yyyy-MM-dd");
}
function isWeekendDay(dateObj) {
  const d = dateObj.getDay();
  return d === 0 || d === 6;
}
function isPastDay(dateStr) {
  try {
    const today = startOfToday();
    const d = parseISO(dateStr);
    return d < today;
  } catch {
    return false;
  }
}

function parseMinutes(hhmm) {
  const s = String(hhmm || "").trim();
  const m = /^(\d{2}):(\d{2})$/.exec(s);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return hh * 60 + mm;
}
function minutesToDisplay(hhmm) {
  const mins = parseMinutes(hhmm);
  if (mins == null) return "";
  const h24 = Math.floor(mins / 60);
  const m = mins % 60;
  const ampm = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const mm = String(m).padStart(2, "0");
  return `${h12}:${mm} ${ampm}`;
}

function normalizeType(type, notesOrReason) {
  let t = String(type || "").trim().toLowerCase();
  const rr = String(notesOrReason || "").trim().toLowerCase();

  if (!t) {
    if (rr.includes("work from home") || rr.includes("wfh")) t = TYPES.WFH;
    else if (rr.includes("flex") || rr.includes("personal")) t = TYPES.FLEX;
    else if (rr.includes("sick")) t = TYPES.SICK;
    else if (rr.includes("meeting")) t = TYPES.MEETING_WITH_CLIENT;
    else if (rr.includes("education") || rr.includes("webinar")) t = TYPES.EDUCATION;
    else if (rr.includes("industry")) t = TYPES.INDUSTRY_EVENT;
    else if (rr.includes("weekly")) t = TYPES.WEEKLY_DAY_OFF;
    else if (rr.includes("closed")) t = TYPES.OFFICE_CLOSED;
    else if (rr.includes("early")) t = TYPES.CLOSING_EARLY;
    else if (rr.includes("full day") || rr.includes("full-day") || rr.includes("full_day")) t = TYPES.FULL_DAY;
    else t = TYPES.VACATION;
  }

  if (t === "work_from_home" || t === "work-from-home" || t === "workfromhome") t = TYPES.WFH;
  if (t === "full-day" || t === "full day" || t === "full_day" || t === "fullday") t = TYPES.FULL_DAY;
  if (t === "flex" || t === "flex day" || t === "personal" || t === "personal day") t = TYPES.FLEX;
  if (t === "sick" || t === "sick day") t = TYPES.SICK;
  if (t === "webinar") t = TYPES.EDUCATION;

  if (!Object.values(TYPES).includes(t)) return TYPES.VACATION;
  return t;
}

function isAdminOnlyType(type) {
  return (
    type === TYPES.OFFICE_CLOSED ||
    type === TYPES.CLOSING_EARLY ||
    type === TYPES.INDUSTRY_EVENT ||
    type === TYPES.WEEKLY_DAY_OFF ||
    type === TYPES.FULL_DAY
  );
}

function isGlobalNoteType(type) {
  return (
    type === TYPES.OFFICE_CLOSED ||
    type === TYPES.CLOSING_EARLY ||
    type === TYPES.INDUSTRY_EVENT
  );
}

function countsTowardGoneCapacity(type) {
  return type === TYPES.VACATION || type === TYPES.WEEKLY_DAY_OFF || type === TYPES.FLEX || type === TYPES.SICK;
}

function displayTimeOnly(r) {
  const a = parseMinutes(r.startTime);
  const b = parseMinutes(r.endTime);

  if (a != null && b != null && b > a) {
    const da = minutesToDisplay(r.startTime);
    const dbb = minutesToDisplay(r.endTime);
    return da && dbb ? `${da}–${dbb}` : "Time";
  }

  if ((r.kind || "fullday") === "fullday") return "All Day";
  return "Time";
}

function defaultNoteForType(type) {
  const t = String(type || "").trim().toLowerCase();
  if (t === TYPES.WFH) return "Working From Home";
  if (t === TYPES.VACATION) return "Vacation";
  if (t === TYPES.FLEX) return "Flex Day";
  if (t === TYPES.SICK) return "Sick Day";
  if (t === TYPES.WEEKLY_DAY_OFF) return "Vacation Day (Not Counted)";
  if (t === TYPES.FULL_DAY) return "Full Day";
  if (t === TYPES.EDUCATION) return "Education/Webinar";
  return "";
}

function normalizeReq(r) {
  const status = String(r.status || "approved").toLowerCase();
  const email = r.email || "";
  const emailLower = r.emailLower || (email ? toEmailLower(email) : "");
  const notes = r.notes != null ? r.notes : r.reason || "";
  const type = normalizeType(r.type, notes);

  let kind = r.kind || "fullday";
  let startTime = r.startTime || r.start || "";
  let endTime = r.endTime || r.end || "";

  if (!r.kind && (r.duration === "am" || r.duration === "pm")) {
    kind = "time";
    startTime = r.duration === "am" ? "09:00" : "13:00";
    endTime = r.duration === "am" ? "13:00" : "17:00";
  }

  if (type === TYPES.FULL_DAY) {
    kind = "fullday";
    startTime = "";
    endTime = "";
  }

  const a = parseMinutes(startTime);
  const b = parseMinutes(endTime);
  if (
    a != null &&
    b != null &&
    b > a &&
    kind !== "time" &&
    type !== TYPES.WFH &&
    type !== TYPES.FULL_DAY &&
    !isGlobalNoteType(type)
  ) {
    kind = "time";
  }

  const employeeName =
    r.employeeName || r.name || r.displayName || (email ? email : "");

  return {
    ...r,
    status,
    email,
    emailLower,
    notes: String(notes || "").trim(),
    type,
    kind,
    startTime,
    endTime,
    employeeName: String(employeeName || "").trim(),
  };
}

function hoverText(r) {
  const note = String(r.notes || "").trim();
  
  if (isGlobalNoteType(r.type)) return note || "";

  let typeName = "Vacation";
  if (r.type === TYPES.WFH) typeName = "WFH";
  else if (r.type === TYPES.MEETING_WITH_CLIENT) typeName = "Meeting";
  else if (r.type === TYPES.EDUCATION) typeName = "Education";
  else if (r.type === TYPES.FULL_DAY) typeName = "Full Day";
  else if (r.type === TYPES.FLEX) typeName = "Flex Day";
  else if (r.type === TYPES.SICK) typeName = "Sick Day";
  
  return note ? `${typeName} - ${note}` : typeName;
}

function getChipContent(r) {
  const name = r.employeeName;
  const time = displayTimeOnly(r);
  return { top: name, bottom: time };
}

function chipVacationFamily() {
  return "bg-sky-50 border-sky-200 text-sky-900";
}
function chipWFH() {
  return "bg-amber-50 border-amber-300 text-amber-900";
}
function chipMeeting() {
  return "bg-pink-50 border-pink-200 text-pink-900";
}
function chipEducation() {
  return "bg-violet-50 border-violet-200 text-violet-900";
}
function chipFullDayGreen() {
  return "bg-green-50 border-green-200 text-green-900";
}
function chipSick() {
  return "bg-stone-100 border-stone-200 text-stone-700";
}

function chipClasses(r, isMine, isAdmin) {
  let baseClass = "bg-white border-gray-200 text-gray-800";

  if (r.type === TYPES.FULL_DAY) baseClass = chipFullDayGreen();
  else if (r.type === TYPES.WFH) baseClass = chipWFH();
  else if (r.type === TYPES.MEETING_WITH_CLIENT) baseClass = chipMeeting();
  else if (r.type === TYPES.EDUCATION) baseClass = chipEducation();
  else if (r.type === TYPES.SICK) baseClass = chipSick();
  else if (
    r.type === TYPES.VACATION ||
    r.type === TYPES.FLEX ||
    r.type === TYPES.WEEKLY_DAY_OFF
  ) {
    baseClass = chipVacationFamily();
  }

  if (isMine && !isAdmin) {
    if (r.status === "pending") baseClass = "bg-yellow-50 border-yellow-200 text-yellow-900";
    if (r.status === "denied") baseClass = "bg-gray-100 border-gray-200 text-gray-500 line-through";
  }

  if (isMine) {
    return `${baseClass} border-2 border-black ring-0`; 
  }

  return baseClass;
}

function yearOf(dateStr) {
  try {
    return parseISO(dateStr).getFullYear();
  } catch {
    return null;
  }
}

function computeUsageThisYear(myRequests, yearNow) {
  const normalized = myRequests.map(normalizeReq);

  let vacFullDays = 0;
  let vacHours = 0;

  let flexFullDays = 0;
  let flexHours = 0;
  
  let sickFullDays = 0;
  let sickHours = 0;

  let wfhFullDays = 0;
  let wfhHours = 0;

  for (const r of normalized) {
    if (!r.date) continue;
    if (yearOf(r.date) !== yearNow) continue;
    if (r.status === "denied") continue;

    const isVacation = r.type === TYPES.VACATION;
    const isFlex = r.type === TYPES.FLEX;
    const isSick = r.type === TYPES.SICK;
    const isWFH = r.type === TYPES.WFH;

    if (!isVacation && !isFlex && !isWFH && !isSick) continue;

    let dayVal = 0;
    let hourVal = 0;

    if (r.kind === "fullday") {
      dayVal = 1;
    } else {
      const a = parseMinutes(r.startTime);
      const b = parseMinutes(r.endTime);
      if (a != null && b != null && b > a) {
        const h = (b - a) / 60;
        if (h >= COUNTABLE_VACATION_MIN_HOURS) hourVal = h;
      }
    }

    if (isVacation) {
      vacFullDays += dayVal;
      vacHours += hourVal;
    } else if (isFlex) {
      flexFullDays += dayVal;
      flexHours += hourVal;
    } else if (isSick) {
      sickFullDays += dayVal;
      sickHours += hourVal;
    } else if (isWFH) {
      wfhFullDays += dayVal;
      wfhHours += hourVal;
    }
  }

  return {
    vacationTotal: vacFullDays + vacHours / 8,
    flexTotal: flexFullDays + flexHours / 8,
    sickTotal: sickFullDays + sickHours / 8,
    wfhTotal: wfhFullDays + wfhHours / 8,
  };
}

function countableVacationDays(req) {
  const r = normalizeReq(req || {});
  if (r.status === "denied" || r.type !== TYPES.VACATION) return 0;
  if (r.kind === "fullday") return 1;

  const start = parseMinutes(r.startTime);
  const end = parseMinutes(r.endTime);
  if (start == null || end == null || end <= start) return 0;

  const hours = (end - start) / 60;
  return hours >= COUNTABLE_VACATION_MIN_HOURS ? hours / 8 : 0;
}

function formatBalanceDays(value) {
  const days = Number(value || 0);
  return Math.abs(days - Math.round(days)) < 1e-9
    ? String(Math.round(days))
    : days.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

function entrySortKey(r) {
  const rr = normalizeReq(r);

  const isVacationFamily =
    rr.type === TYPES.VACATION || rr.type === TYPES.FLEX || rr.type === TYPES.SICK || rr.type === TYPES.WEEKLY_DAY_OFF;

  const isAllDay = (rr.kind || "fullday") === "fullday";
  const startMins = parseMinutes(rr.startTime);
  const hasTime = rr.kind === "time" && startMins != null;

  if (isVacationFamily && isAllDay) {
    return { bucket: 0, minutes: 0, name: rr.employeeName || "" };
  }
  if (hasTime) {
    return { bucket: 1, minutes: startMins, name: rr.employeeName || "" };
  }
  if (isAllDay && rr.type !== TYPES.FULL_DAY) {
    return { bucket: 2, minutes: 0, name: rr.employeeName || "" };
  }
  if (rr.type === TYPES.FULL_DAY) {
    return { bucket: 3, minutes: 0, name: rr.employeeName || "" };
  }

  return { bucket: 4, minutes: 9999, name: rr.employeeName || "" };
}

export default function AvailabilityCalendar({ user, isAdmin: isAdminProp = false }) {
  const meEmail = user?.email || "";
  const meEmailLower = toEmailLower(meEmail);
  const role = String(user?.role || "").toLowerCase();
  const isAdmin = isAdminProp || role === "admin" || role === "developer";
  const isEsther = meEmailLower === ESTHER_EMAIL_LOWER;

  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [approvedInView, setApprovedInView] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");

  const [activeReq, setActiveReq] = useState(null);
  const [editDate, setEditDate] = useState(null);

  const [form, setForm] = useState({
    startDate: "",
    endDate: "",
    employeeEmailLower: meEmailLower,
    employeeEmail: meEmail,
    employeeName: user?.displayName || user?.name || meEmail,
    type: TYPES.VACATION,
    kind: "fullday",
    startTime: "09:00",
    endTime: "17:00",
    notes: "",
    status: isAdmin ? "approved" : "pending",
  });

  useEffect(() => {
    if (!isAdmin) return;
    const unsub = onSnapshot(collection(db, "users"), (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const cleaned = data
        .map((u) => ({
          id: u.id,
          email: u.email || "",
          emailLower: u.emailLower || toEmailLower(u.email || ""),
          name: u.name || u.displayName || u.email || u.id,
        }))
        .filter((u) => (u.emailLower || "").includes("@"));

      const map = new Map();
      for (const u of cleaned) {
        const key = toEmailLower(u.emailLower || u.email);
        if (!map.has(key)) map.set(key, u);
      }
      const unique = Array.from(map.values()).sort((a, b) =>
        String(a.name).localeCompare(String(b.name))
      );
      setAllUsers(unique);
    });
    return () => unsub();
  }, [isAdmin]);

  useEffect(() => {
    const first = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 });
    const last = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 0 });

    const qApproved = query(
      collection(db, "timeOffRequests"),
      where("status", "==", "approved"),
      where("date", ">=", ymd(first)),
      where("date", "<=", ymd(last))
    );

    const unsub = onSnapshot(qApproved, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setApprovedInView(list);
    });

    return () => unsub();
  }, [currentMonth]);

  useEffect(() => {
    if (!meEmailLower) return;
    const qMine = query(
      collection(db, "timeOffRequests"),
      where("emailLower", "==", meEmailLower)
    );
    const unsub = onSnapshot(qMine, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setMyRequests(list);
    });
    return () => unsub();
  }, [meEmailLower]);

  const firstVisible = useMemo(
    () => startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 }),
    [currentMonth]
  );
  const lastVisible = useMemo(
    () => endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 0 }),
    [currentMonth]
  );

  const normalizedApproved = useMemo(
    () => approvedInView.map(normalizeReq),
    [approvedInView]
  );

  const normalizedInView = useMemo(() => {
    const map = new Map();
    for (const r of normalizedApproved) map.set(r.id, r);

    const mineNormalized = myRequests.map(normalizeReq).filter((r) => {
      if (!r.date) return false;
      try {
        const d = parseISO(r.date);
        return d >= firstVisible && d <= lastVisible;
      } catch {
        return false;
      }
    });

    for (const r of mineNormalized) map.set(r.id, r);
    return Array.from(map.values());
  }, [normalizedApproved, myRequests, firstVisible, lastVisible]);

  const rawGoneCountByDate = useMemo(() => {
    const m = new Map();
    for (const r of normalizedApproved) {
      if (!r.date) continue;
      if (!countsTowardGoneCapacity(r.type)) continue;
      m.set(r.date, (m.get(r.date) || 0) + 1);
    }
    return m;
  }, [normalizedApproved]);

  const coverageCreditByDate = useMemo(() => {
    const m = new Map();
    for (const r of normalizedApproved) {
      if (!r.date) continue;
      if (r.type !== TYPES.FULL_DAY) continue;
      m.set(r.date, (m.get(r.date) || 0) + 1);
    }
    return m;
  }, [normalizedApproved]);

  const effectiveGoneCountByDate = useMemo(() => {
    const allDates = new Set([
      ...Array.from(rawGoneCountByDate.keys()),
      ...Array.from(coverageCreditByDate.keys()),
    ]);
    const m = new Map();
    for (const d of allDates) {
      const raw = rawGoneCountByDate.get(d) || 0;
      const credit = coverageCreditByDate.get(d) || 0;
      m.set(d, Math.max(0, raw - credit));
    }
    return m;
  }, [rawGoneCountByDate, coverageCreditByDate]);

  const globalNotesByDate = useMemo(() => {
    const m = new Map();
    for (const r of normalizedApproved) {
      if (!r.date) continue;
      if (!isGlobalNoteType(r.type)) continue;

      const note = String(r.notes || "").trim();
      const cur = m.get(r.date) || {
        closedNote: "",
        earlyNote: "",
        industryNote: "",
        closedReq: null,
        earlyReq: null,
        industryReq: null,
      };

      if (r.type === TYPES.OFFICE_CLOSED && !cur.closedNote) {
        cur.closedNote = note;
        cur.closedReq = r;
      }
      if (r.type === TYPES.CLOSING_EARLY && !cur.earlyNote) {
        cur.earlyNote = note;
        cur.earlyReq = r;
      }
      if (r.type === TYPES.INDUSTRY_EVENT && !cur.industryNote) {
        cur.industryNote = note;
        cur.industryReq = r;
      }
      m.set(r.date, cur);
    }
    return m;
  }, [normalizedApproved]);

  const closedDays = useMemo(() => {
    const s = new Set();
    for (const r of normalizedApproved) {
      if (r.type === TYPES.OFFICE_CLOSED) s.add(r.date);
    }
    return s;
  }, [normalizedApproved]);

  const visibleByDate = useMemo(() => {
    const m = new Map();
    for (const r of normalizedInView) {
      if (!m.has(r.date)) m.set(r.date, []);
      m.get(r.date).push(r);
    }

    for (const [k, arr] of m.entries()) {
      arr.sort((a, b) => {
        const aa = entrySortKey(a);
        const bb = entrySortKey(b);
        if (aa.bucket !== bb.bucket) return aa.bucket - bb.bucket;
        if (aa.minutes !== bb.minutes) return aa.minutes - bb.minutes;
        return String(aa.name).localeCompare(String(bb.name));
      });
      m.set(k, arr);
    }
    return m;
  }, [normalizedInView]);

  function prevMonth() {
    setCurrentMonth(subDays(startOfMonth(currentMonth), 1));
  }
  function nextMonth() {
    setCurrentMonth(addDays(endOfMonth(currentMonth), 1));
  }

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const rows = [];
  let days = [];
  let day = startDate;
  while (day <= endDate) {
    for (let i = 0; i < 7; i++) {
      days.push(day);
      day = addDays(day, 1);
    }
    rows.push(days);
    days = [];
  }

  function openCreateModal(dateObj) {
    if (isWeekendDay(dateObj)) return;
    const dateStr = ymd(dateObj);

    if (closedDays.has(dateStr) && !isAdmin) {
      alert("This day is marked as Office Closed. No bookings can be made.");
      return;
    }

    setModalMode("create");
    setActiveReq(null);
    setEditDate(null);

    setForm({
      startDate: dateStr,
      endDate: dateStr,
      employeeEmailLower: isAdmin ? "" : meEmailLower,
      employeeEmail: isAdmin ? "" : meEmail,
      employeeName: isAdmin ? "" : (user?.displayName || user?.name || meEmail),
      type: TYPES.VACATION,
      kind: "fullday",
      startTime: "09:00",
      endTime: "17:00",
      notes: defaultNoteForType(TYPES.VACATION),
      status: isAdmin ? "approved" : "pending",
    });
    setModalOpen(true);
  }

  function openEditModal(req) {
    const r = normalizeReq(req);
    setActiveReq(r);
    setEditDate(r.date);
    setModalMode("edit");

    setForm({
      startDate: r.date,
      endDate: r.date,
      employeeEmailLower: r.emailLower || "",
      employeeEmail: r.email || "",
      employeeName: r.employeeName || "",
      type: r.type || TYPES.VACATION,
      kind: r.kind || "fullday",
      startTime: r.startTime || "09:00",
      endTime: r.endTime || "17:00",
      notes: r.notes || "",
      status: r.status || "pending",
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setActiveReq(null);
    setEditDate(null);
  }

  function canEdit(req) {
    const r = normalizeReq(req);
    const isMine = toEmailLower(r.emailLower) === meEmailLower;
    if (isAdmin) return true;
    if (isGlobalNoteType(r.type)) return false;
    if (r.type === TYPES.FULL_DAY) {
      if (!isEsther) return false;
      if (!isMine) return false;
      if (isPastDay(r.date)) return false;
      return true;
    }
    if (isAdminOnlyType(r.type)) return false;
    if (!isMine) return false;
    if (isPastDay(r.date)) return false;
    return true;
  }

  function canSaveCurrent() {
    if (isAdmin) return true;
    const t = normalizeType(form.type, form.notes);
    if (t === TYPES.FULL_DAY) return false;
    return true;
  }

  function resolveEmployeeForSave(type) {
    if (isAdmin && isGlobalNoteType(type)) {
      return { emailLower: "__global__", email: "", employeeName: "" };
    }
    if (type === TYPES.FULL_DAY) {
      const chosenLower = toEmailLower(form.employeeEmailLower || form.employeeEmail || "");
      const u = allUsers.find((x) => toEmailLower(x.emailLower || x.email) === chosenLower);
      const email = String(u?.email || form.employeeEmail || "").trim();
      const emailLower = toEmailLower(u?.emailLower || email);
      const employeeName = String(u?.name || form.employeeName || email || "").trim() || emailLower;
      return { emailLower, email, employeeName };
    }
    if (!isAdmin) {
      return {
        emailLower: meEmailLower,
        email: meEmail,
        employeeName: user?.displayName || user?.name || meEmail,
      };
    }
    const chosenLower = toEmailLower(form.employeeEmailLower || form.employeeEmail || "");
    const u = allUsers.find((x) => toEmailLower(x.emailLower || x.email) === chosenLower);
    const email = String(u?.email || form.employeeEmail || "").trim();
    const emailLower = toEmailLower(u?.emailLower || email);
    const employeeName = String(u?.name || form.employeeName || email || "").trim() || emailLower;
    return { emailLower, email, employeeName };
  }

  const yearNow = new Date().getFullYear();
  const usageStats = useMemo(
    () => computeUsageThisYear(myRequests, yearNow),
    [myRequests, yearNow]
  );

  const hasInfiniteDaysOff = useMemo(() => {
    const raw = user?.annualAllotmentDays;
    if (raw === Infinity) return true;
    const s = String(raw ?? "").trim().toLowerCase();
    if (s === "-" || s === "infinite" || s === "infinity" || s === "unlimited" || s === "∞") return true;
    return false;
  }, [user?.annualAllotmentDays]);

  const annualAllotmentDays = Number(user?.annualAllotmentDays ?? 0) || 0;
  const flexAllotmentDays = Number(user?.flexAllotmentDays ?? 3) || 0;
  const sickAllotmentDays = Number(user?.sickAllotmentDays ?? 10) || 0;
  const wfhAllotmentDays = Number(user?.wfhAllotmentDays ?? 0) || 0;

  const vacPct = useMemo(() => {
    if (!annualAllotmentDays) return 0;
    return Math.min(100, Math.max(0, (usageStats.vacationTotal / annualAllotmentDays) * 100));
  }, [usageStats.vacationTotal, annualAllotmentDays]);

  const flexQuotaUsedUp = useMemo(() => {
    if (isAdmin) return false;
    if (!flexAllotmentDays) return false;
    return usageStats.flexTotal >= flexAllotmentDays - 1e-9;
  }, [isAdmin, usageStats.flexTotal, flexAllotmentDays]);

  const sickQuotaUsedUp = useMemo(() => {
    if (isAdmin) return false;
    if (!sickAllotmentDays) return false;
    return usageStats.sickTotal >= sickAllotmentDays - 1e-9;
  }, [isAdmin, usageStats.sickTotal, sickAllotmentDays]);

  const wfhQuotaUsedUp = useMemo(() => {
    if (isAdmin) return false;
    if (!wfhAllotmentDays) return false;
    return usageStats.wfhTotal >= wfhAllotmentDays - 1e-9;
  }, [isAdmin, usageStats.wfhTotal, wfhAllotmentDays]);

  const currentTypeNormalized = normalizeType(form.type, form.notes);
  const hideEmployeePicker = isAdmin && isGlobalNoteType(currentTypeNormalized);

  async function saveRequest() {
    const type = normalizeType(form.type, form.notes);
    if (!isAdmin && type === TYPES.FLEX && flexQuotaUsedUp) {
      alert("You’ve used all of your Flex Days for the year.");
      return;
    }
    if (!isAdmin && type === TYPES.SICK && sickQuotaUsedUp) {
      alert("You’ve used all of your Sick Days for the year.");
      return;
    }
    if (!isAdmin && type === TYPES.WFH && wfhQuotaUsedUp) {
      alert("You’ve used all of your Work From Home days for the year.");
      return;
    }

    let datesToProcess = [];
    if (modalMode === "edit") {
      datesToProcess = [editDate];
    } else {
      try {
        const start = parseISO(form.startDate);
        const end = parseISO(form.endDate);
        if (end < start) {
          alert("End date cannot be before start date.");
          return;
        }
        const range = eachDayOfInterval({ start, end });

        datesToProcess = range
          .filter((d) => !isWeekend(d))
          .map((d) => ymd(d));

        if (datesToProcess.length === 0) {
          alert("No valid weekdays selected in this range.");
          return;
        }
      } catch (e) {
        console.error("Date parsing error", e);
        alert("Invalid date range.");
        return;
      }
    }

    if (type === TYPES.SICK && !isAdmin) {
      const today = startOfToday();
      for (const dStr of datesToProcess) {
        const dObj = parseISO(dStr);
        if (dObj > today) {
          alert("You cannot schedule Sick Days in the future.");
          return;
        }
      }
    }

    if (type === TYPES.FULL_DAY && !isAdmin) {
      alert("Full Day entries are created by admins.");
      return;
    }
    if (type === TYPES.FULL_DAY) {
      const chosenLower = toEmailLower(form.employeeEmailLower || form.employeeEmail || "");
      if (chosenLower !== ESTHER_EMAIL_LOWER) {
        alert("Full Day can only be assigned to Esther Mandel.");
        return;
      }
    }
    if (!isAdmin && isAdminOnlyType(type)) {
      alert("That type can only be added by admins.");
      return;
    }

    const { emailLower, email, employeeName } = resolveEmployeeForSave(type);
    if (!isGlobalNoteType(type) && (!emailLower || !emailLower.includes("@"))) {
      alert("Select an employee.");
      return;
    }

    const notesTrim = String(form.notes || "").trim();
    const finalNotes = notesTrim ? notesTrim : defaultNoteForType(type);
    const forceNoTime = isGlobalNoteType(type) || type === TYPES.WFH || type === TYPES.FULL_DAY || type === TYPES.SICK;
    const wantsTime = String(form.kind || "").toLowerCase() === "time";
    const startTime = String(form.startTime || "").trim();
    const endTime = String(form.endTime || "").trim();

    const a = parseMinutes(startTime);
    const b = parseMinutes(endTime);
    const hasValidTimeRange = a != null && b != null && b > a;
    const finalKind = forceNoTime ? "fullday" : wantsTime ? "time" : "fullday";

    if (!forceNoTime && finalKind === "time" && !hasValidTimeRange) {
      alert("Pick a valid time range.");
      return;
    }

    if (!isAdmin && type === TYPES.VACATION && !hasInfiniteDaysOff) {
      const requestedHoursPerDate = finalKind === "time" ? (b - a) / 60 : 8;
      const requestedDaysPerDate =
        finalKind === "time" && requestedHoursPerDate < COUNTABLE_VACATION_MIN_HOURS
          ? 0
          : requestedHoursPerDate / 8;

      if (requestedDaysPerDate > 0) {
        const original = modalMode === "edit" && activeReq ? normalizeReq(activeReq) : null;
        const originalYear = original?.date ? yearOf(original.date) : null;
        const originalDays = original ? countableVacationDays(original) : 0;
        const requestedYears = [...new Set(datesToProcess.map(yearOf).filter(Number.isFinite))];

        for (const requestYear of requestedYears) {
          const usedIncludingOriginal = computeUsageThisYear(myRequests, requestYear).vacationTotal;
          const originalDaysInYear = originalYear === requestYear ? originalDays : 0;
          const usedBeforeRequest = Math.max(0, usedIncludingOriginal - originalDaysInYear);
          const datesInYear = datesToProcess.filter((dateStr) => yearOf(dateStr) === requestYear);
          const requestedDaysInYear = datesInYear.length * requestedDaysPerDate;
          const projectedUsedDays = usedBeforeRequest + requestedDaysInYear;
          const increasesVacationUsage = requestedDaysInYear > originalDaysInYear + 1e-9;

          if (increasesVacationUsage && projectedUsedDays > annualAllotmentDays + 1e-9) {
            const remainingBeforeRequest = annualAllotmentDays - usedBeforeRequest;
            const overByDays = projectedUsedDays - annualAllotmentDays;
            const datesList = datesToProcess.join(", ");
            const durationText =
              finalKind === "time"
                ? `${requestedHoursPerDate.toFixed(2).replace(/0+$/, "").replace(/\.$/, "")} hours per day`
                : `${datesToProcess.length} full day${datesToProcess.length === 1 ? "" : "s"}`;

            const subject = encodeURIComponent(
              `Vacation exception request: ${employeeName} — ${datesList}`
            );
            const body = encodeURIComponent(
              `Hi Ben,\n\nI attempted to submit a vacation request that would exceed my available vacation balance.\n\nEmployee: ${employeeName}\nRequested date(s): ${datesList}\nDuration: ${durationText}\nAnnual vacation allotment: ${formatBalanceDays(annualAllotmentDays)} days\nVacation used or already requested before this request: ${formatBalanceDays(usedBeforeRequest)} days\nBalance before this request: ${formatBalanceDays(remainingBeforeRequest)} days\nAmount requested: ${formatBalanceDays(requestedDaysInYear)} days\nAmount over allotment if approved: ${formatBalanceDays(overByDays)} days\nNotes: ${finalNotes || "None"}\n\nPlease let me know whether an exception can be approved.\n\nI understand that approved vacation beyond my allotted paid vacation may result in unpaid time or a payroll adjustment or recovery for vacation pay paid in advance, and may affect discretionary bonus eligibility for salaried employees, subject to my employment agreement, company policy, applicable employment standards, and any required notice or authorization.\n\nThanks,\n${employeeName}`
            );

            alert(
              `This vacation request would exceed your available vacation balance by ${formatBalanceDays(overByDays)} day${Math.abs(overByDays - 1) < 1e-9 ? "" : "s"}. Requests of 2 hours or more cannot be submitted when they exceed your allotted vacation. Please contact Ben. A pre-filled email will open so you can request an exception.`
            );
            window.location.href = `mailto:${VACATION_EXCEPTION_CONTACT}?subject=${subject}&body=${body}`;
            return;
          }
        }
      }
    }

    const batch = writeBatch(db);
    const pendingDates = [];

    for (const dateStr of datesToProcess) {
      if (closedDays && closedDays.has && closedDays.has(dateStr) && type !== TYPES.OFFICE_CLOSED && !isAdmin) {
        if (modalMode === "edit") {
          alert("This day is marked as Office Closed.");
          return;
        }
        continue;
      }

      const approvedGone = effectiveGoneCountByDate.get(dateStr) || 0;
      const goneType = countsTowardGoneCapacity(type);

      let status = String(form.status || "pending").toLowerCase();
      if (isAdmin && isGlobalNoteType(type)) {
        status = "approved";
      } else if (!isAdmin) {
        if (!goneType) status = "approved";
        else status = approvedGone >= 2 ? "pending" : "approved";
      }

      if (status === "pending") {
        pendingDates.push(dateStr);
      }

      const payload = {
        date: dateStr,
        type,
        kind: finalKind,
        startTime: !forceNoTime && finalKind === "time" ? startTime : "",
        endTime: !forceNoTime && finalKind === "time" ? endTime : "",
        start: !forceNoTime && finalKind === "time" ? startTime : "",
        end: !forceNoTime && finalKind === "time" ? endTime : "",
        notes: finalNotes,
        reason: finalNotes,
        status,
        email,
        emailLower,
        employeeName,
        updatedAt: new Date().toISOString(),
        createdBy: isAdmin ? (user?.emailLower || meEmailLower) : meEmailLower,
      };

      if (modalMode === "create") {
        const newRef = doc(collection(db, "timeOffRequests"));
        batch.set(newRef, payload);
      } else if (modalMode === "edit" && activeReq?.id) {
        const ref = doc(db, "timeOffRequests", activeReq.id);
        batch.update(ref, payload);
      }
    }

    try {
      await batch.commit();

      // Trigger the local mailto link if any requests were marked as pending
      if (pendingDates.length > 0) {
        const datesList = pendingDates.join(", ");
        const subject = encodeURIComponent(`URGENT: Time Off Approval Needed for ${employeeName}`);
        const body = encodeURIComponent(
          `Hi Benjamin,\n\nI have submitted a time-off request that requires your approval for the following date(s): ${datesList}.\n\nType: ${type}\nNotes: ${finalNotes || "None"}\nDuration: ${finalKind === "time" ? `${startTime} - ${endTime}` : "All Day"}\n\nPlease review and approve in the Admin portal.\n\nThanks,\n${employeeName}`
        );
        
        window.location.href = `mailto:benjamin@mcins.ca?subject=${subject}&body=${body}`;
      }

      closeModal();
    } catch (e) {
      console.error("Save request failed:", e);
      alert("Failed to save.");
    }
  }

  async function deleteRequest(req) {
    if (!req?.id) return;
    if (!canEdit(req)) return;
    if (!window.confirm("Delete this entry?")) return;
    try {
      await deleteDoc(doc(db, "timeOffRequests", req.id));
      closeModal();
    } catch (e) {
      console.error("Delete failed:", e);
      alert("Failed to delete.");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xl font-semibold">{format(currentMonth, "MMMM yyyy")}</div>
        <div className="flex gap-2">
          <button onClick={prevMonth} className="px-3 py-1 rounded border bg-white hover:bg-gray-50">
            Prev
          </button>
          <button onClick={nextMonth} className="px-3 py-1 rounded border bg-white hover:bg-gray-50">
            Next
          </button>
        </div>
      </div>

      <div className="border rounded-xl p-4 bg-white shadow-sm space-y-3">
        {/* Vacation Progress Bar */}
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
            <div className="font-semibold text-sm">Vacation Days ({yearNow})</div>
            <div className="text-xs text-gray-700">
              {usageStats.vacationTotal.toFixed(2)} used{annualAllotmentDays ? ` of ${annualAllotmentDays}` : ""}
            </div>
          </div>
          <div className="mt-1 h-2 bg-gray-200 rounded">
            <div
              className="h-2 bg-[#1B80BA] rounded"
              style={{ width: `${annualAllotmentDays ? vacPct : 0}%` }}
            />
          </div>
        </div>

        {/* Text Stats for Flex and Sick */}
        <div className="flex gap-4 pt-1 text-sm text-gray-600 font-medium">
          {!hasInfiniteDaysOff && (
            <div>
              <span className="text-purple-700">Flex Days:</span> {usageStats.flexTotal.toFixed(0)} out of {flexAllotmentDays}
            </div>
          )}
          <div>
            <span className="text-stone-700">Sick Days:</span> {usageStats.sickTotal.toFixed(0)} out of {sickAllotmentDays}
          </div>
        </div>

        {/* Legend */}
        <div className="pt-3 mt-2 border-t border-gray-100 flex flex-wrap gap-4 text-xs text-gray-600">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-sky-50 border border-sky-200"></span> Vacation / Flex
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-pink-50 border border-pink-200"></span> Meeting
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-stone-100 border border-stone-200"></span> Sick Day
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-amber-50 border border-amber-300"></span> WFH
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-violet-50 border border-violet-200"></span> Education
          </div>
        </div>
      </div>

      <div className="border rounded-xl overflow-hidden bg-white shadow-sm">
        <div className="grid grid-cols-7 bg-gray-50 border-b">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="p-2 text-xs font-semibold text-gray-600">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {rows.flat().map((dayObj, idx) => {
            const dateStr = ymd(dayObj);
            const inMonth = isSameMonth(dayObj, monthStart);
            const today = isSameDay(dayObj, new Date());
            const weekend = isWeekendDay(dayObj);

            const entries = visibleByDate.get(dateStr) || [];
            const goneCount = effectiveGoneCountByDate.get(dateStr) || 0;

            const notes = globalNotesByDate.get(dateStr) || {
              closedNote: "",
              earlyNote: "",
              industryNote: "",
              closedReq: null,
              earlyReq: null,
              industryReq: null,
            };
            const isClosed = closedDays.has(dateStr);

            let dayBg = "bg-white";
            if (goneCount === 2) dayBg = "bg-yellow-50";
            if (goneCount >= 3) dayBg = "bg-red-50";
            if (!inMonth) dayBg = "bg-gray-50";
            if (isClosed) dayBg = "bg-red-100";

            const showPlus =
              inMonth &&
              !weekend &&
              !isClosed &&
              goneCount < 4;

            return (
              <div
                key={idx}
                className={[
                  "min-h-[110px] border-r border-b p-2 relative",
                  dayBg,
                  weekend ? "opacity-70" : "",
                ].join(" ")}
              >
                <div className="flex items-center justify-between">
                  <div
                    className={[
                      "text-sm font-semibold inline-flex items-center justify-center w-7 h-7 rounded",
                      today ? "bg-[#1B80BA] text-white" : inMonth ? "text-gray-800" : "text-gray-400",
                    ].join(" ")}
                    title={today ? "Today" : ""}
                  >
                    {format(dayObj, "d")}
                  </div>

                  {showPlus && (
                    <button
                      onClick={() => openCreateModal(dayObj)}
                      className="text-xs px-2 py-1 rounded border bg-white hover:bg-gray-50"
                      title="Add entry"
                    >
                      +
                    </button>
                  )}
                </div>

                {notes.closedNote !== "" || isClosed ? (
                  <div
                    className={[
                      "mt-2 text-xs px-2 py-1 rounded bg-red-200 text-red-900 border border-red-300",
                      isAdmin ? "cursor-pointer hover:bg-red-300" : "",
                    ].join(" ")}
                    title={notes.closedNote || "Office Closed"}
                    onClick={() => {
                      if (isAdmin && notes.closedReq) openEditModal(notes.closedReq);
                    }}
                  >
                    🚫 Office Closed{notes.closedNote ? `: ${notes.closedNote}` : ""}
                  </div>
                ) : null}

                {notes.earlyNote ? (
                  <div
                    className={[
                      "mt-2 text-xs px-2 py-1 rounded bg-amber-100 text-amber-900 border border-amber-200",
                      isAdmin ? "cursor-pointer hover:bg-amber-200" : "",
                    ].join(" ")}
                    title={notes.earlyNote}
                    onClick={() => {
                      if (isAdmin && notes.earlyReq) openEditModal(notes.earlyReq);
                    }}
                  >
                    🕓 Closing Early: {notes.earlyNote}
                  </div>
                ) : null}

                {notes.industryNote ? (
                  <div
                    className={[
                      "mt-2 text-xs px-2 py-1 rounded bg-violet-100 text-violet-900 border border-violet-200",
                      isAdmin ? "cursor-pointer hover:bg-violet-200" : "",
                    ].join(" ")}
                    title={notes.industryNote}
                    onClick={() => {
                      if (isAdmin && notes.industryReq) openEditModal(notes.industryReq);
                    }}
                  >
                    📌 Industry Event: {notes.industryNote}
                  </div>
                ) : null}

                <div className="mt-2 space-y-1">
                  {entries
                    .filter((r) => !isGlobalNoteType(r.type))
                    .filter((r) => r.type !== TYPES.FULL_DAY || isAdmin || isEsther)
                    .map((rr) => {
                      const isMine = toEmailLower(rr.emailLower) === meEmailLower;
                      const { top, bottom } = getChipContent(rr);

                      return (
                        <div
                          key={rr.id}
                          className={[
                            "text-xs px-2 py-1 rounded border cursor-pointer leading-tight",
                            chipClasses(rr, isMine, isAdmin),
                            canEdit(rr) ? "" : "cursor-default",
                          ].join(" ")}
                          title={hoverText(rr)}
                          onClick={() => {
                            if (canEdit(rr)) openEditModal(rr);
                          }}
                        >
                          <div className="font-medium">{top}</div>
                          <div className="text-[10px] opacity-90">{bottom}</div>
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-4">
          <div className="bg-white w-full max-w-xl rounded-xl shadow-lg p-5">
            <div className="flex items-start justify-between">
              <div className="text-lg font-semibold">
                {modalMode === "create" ? "Add Entry" : "Edit Entry"} (
                {modalMode === "create" ? `${form.startDate}` : `${editDate}`})
              </div>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-800">
                ✕
              </button>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {modalMode === "create" && (
                <div className="flex flex-col md:col-span-2">
                  <label className="text-xs font-semibold text-gray-600 mb-1">Date Range</label>
                  <div className="flex gap-2">
                    <div className="w-full">
                      <span className="text-[10px] text-gray-500 uppercase tracking-wide">From</span>
                      <input
                        type="date"
                        className="border p-2 rounded w-full"
                        value={form.startDate}
                        onChange={(e) => setForm((prev) => ({ ...prev, startDate: e.target.value }))}
                      />
                    </div>
                    <div className="w-full">
                      <span className="text-[10px] text-gray-500 uppercase tracking-wide">To (Inclusive)</span>
                      <input
                        type="date"
                        className="border p-2 rounded w-full"
                        value={form.endDate}
                        onChange={(e) => setForm((prev) => ({ ...prev, endDate: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="text-[11px] text-gray-400 mt-1">
                    Weekends and Office Closed days will be automatically skipped.
                  </div>
                </div>
              )}

              {isAdmin && !hideEmployeePicker && (
                <div className="flex flex-col md:col-span-2">
                  <label className="text-xs font-semibold text-gray-600 mb-1">Employee</label>
                  <select
                    className="border p-2 rounded"
                    value={form.employeeEmailLower || ""}
                    onChange={(e) => {
                      const chosenLower = toEmailLower(e.target.value);
                      const u = allUsers.find((x) => toEmailLower(x.emailLower || x.email) === chosenLower);
                      setForm((prev) => ({
                        ...prev,
                        employeeEmailLower: chosenLower,
                        employeeEmail: u?.email || "",
                        employeeName: u?.name || u?.email || "",
                      }));
                    }}
                  >
                    <option value="">Select…</option>
                    {allUsers
                      .filter((u) => {
                        const isEstherUser = toEmailLower(u.emailLower || u.email) === ESTHER_EMAIL_LOWER;
                        if (currentTypeNormalized === TYPES.FULL_DAY) return isEstherUser;
                        return true;
                      })
                      .map((u) => (
                        <option key={u.id} value={toEmailLower(u.emailLower || u.email)}>
                          {u.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              <div className="flex flex-col">
                <label className="text-xs font-semibold text-gray-600 mb-1">Type</label>
                <select
                  className="border p-2 rounded"
                  value={form.type}
                  disabled={!isAdmin && normalizeType(form.type, form.notes) === TYPES.FULL_DAY}
                  onChange={(e) => {
                    const nextType = e.target.value;

                    setForm((prev) => {
                      const prevType = normalizeType(prev.type, prev.notes);
                      const prevDefault = defaultNoteForType(prevType);
                      const nextNorm = normalizeType(nextType, prev.notes);
                      const nextDefault = defaultNoteForType(nextNorm);

                      const prevNotesTrim = String(prev.notes || "").trim();
                      const shouldReplaceNotes = !prevNotesTrim || (prevNotesTrim === prevDefault && nextDefault);

                      if (isAdmin && isGlobalNoteType(nextType)) {
                        return {
                          ...prev,
                          type: nextType,
                          employeeEmailLower: "",
                          employeeEmail: "",
                          employeeName: "",
                          kind: "fullday",
                          startTime: "09:00",
                          endTime: "17:00",
                          notes: shouldReplaceNotes ? (nextDefault || "") : prev.notes,
                        };
                      }

                      if (isAdmin && nextNorm === TYPES.FULL_DAY) {
                        const estherUser = allUsers.find(
                          (u) => toEmailLower(u.emailLower || u.email) === ESTHER_EMAIL_LOWER
                        );
                        return {
                          ...prev,
                          type: nextType,
                          kind: "fullday",
                          startTime: "09:00",
                          endTime: "17:00",
                          employeeEmailLower: ESTHER_EMAIL_LOWER,
                          employeeEmail: estherUser?.email || ESTHER_EMAIL,
                          employeeName: estherUser?.name || ESTHER_DEFAULT_NAME,
                          notes: shouldReplaceNotes ? (nextDefault || "") : prev.notes,
                        };
                      }

                      return {
                        ...prev,
                        type: nextType,
                        notes: shouldReplaceNotes ? (nextDefault || "") : prev.notes,
                      };
                    });
                  }}
                >
                  <optgroup label="Employee Types">
                    <option value={TYPES.VACATION}>Vacation</option>
                    <option value={TYPES.SICK} disabled={!isAdmin && sickQuotaUsedUp}>
                       Sick Day{!isAdmin && sickQuotaUsedUp ? " (Quota used)" : ""}
                    </option>
                    
                    {!hasInfiniteDaysOff && (
                      <option value={TYPES.FLEX} disabled={!isAdmin && flexQuotaUsedUp}>
                        Flex Day{!isAdmin && flexQuotaUsedUp ? " (Quota used)" : ""}
                      </option>
                    )}
                    
                    <option value={TYPES.WFH} disabled={!isAdmin && wfhQuotaUsedUp}>
                      Work From Home{!isAdmin && wfhQuotaUsedUp ? " (Quota used)" : ""}
                    </option>
                    <option value={TYPES.MEETING_WITH_CLIENT}>Meeting</option>
                    <option value={TYPES.EDUCATION}>Education/Webinar</option>
                  </optgroup>

                  {isAdmin && (
                    <optgroup label="Admin Types">
                      <option value={TYPES.FULL_DAY}>Full Day (Esther Working)</option>
                      <option value={TYPES.WEEKLY_DAY_OFF}>Vacation Day (Not Counted)</option>
                      <option value={TYPES.INDUSTRY_EVENT}>Industry Event (Note)</option>
                      <option value={TYPES.OFFICE_CLOSED}>Office Closed (Note)</option>
                      <option value={TYPES.CLOSING_EARLY}>Closing Early (Note)</option>
                    </optgroup>
                  )}
                </select>

                {!isAdmin && (flexQuotaUsedUp || wfhQuotaUsedUp || sickQuotaUsedUp) && (
                  <div className="text-[11px] text-gray-500 mt-1">
                    Some options are disabled because you’ve used your yearly quota.
                  </div>
                )}
              </div>

              <div className="flex flex-col">
                <label className="text-xs font-semibold text-gray-600 mb-1">Duration</label>
                <select
                  className="border p-2 rounded"
                  value={form.kind}
                  onChange={(e) => setForm((prev) => ({ ...prev, kind: e.target.value }))}
                  disabled={
                    form.type === TYPES.WFH ||
                    form.type === TYPES.FLEX ||
                    form.type === TYPES.SICK ||
                    currentTypeNormalized === TYPES.FULL_DAY ||
                    isGlobalNoteType(currentTypeNormalized)
                  }
                >
                  <option value="fullday">All Day</option>
                  <option value="time">Time Range</option>
                </select>
              </div>

              <div className="flex flex-col md:col-span-2">
                <label className="text-xs font-semibold text-gray-600 mb-1">Time</label>
                {form.kind === "time" &&
                form.type !== TYPES.WFH &&
                form.type !== TYPES.FLEX &&
                form.type !== TYPES.SICK &&
                currentTypeNormalized !== TYPES.FULL_DAY &&
                !isGlobalNoteType(currentTypeNormalized) ? (
                  <div className="flex gap-2">
                    <input
                      className="border p-2 rounded w-full"
                      value={form.startTime}
                      onChange={(e) => setForm((prev) => ({ ...prev, startTime: e.target.value }))}
                      placeholder="09:00"
                    />
                    <input
                      className="border p-2 rounded w-full"
                      value={form.endTime}
                      onChange={(e) => setForm((prev) => ({ ...prev, endTime: e.target.value }))}
                      placeholder="17:00"
                    />
                  </div>
                ) : (
                  <div className="border p-2 rounded bg-gray-50 text-sm">
                    {isGlobalNoteType(currentTypeNormalized)
                      ? "No time (note only)"
                      : currentTypeNormalized === TYPES.FULL_DAY
                      ? "Full Day (no time needed)"
                      : form.type === TYPES.WFH
                      ? "WFH (no time needed)"
                      : form.type === TYPES.FLEX
                      ? "Flex Day (All Day)"
                      : form.type === TYPES.SICK
                      ? "Sick Day (All Day)"
                      : "All Day"}
                  </div>
                )}
              </div>

              <div className="flex flex-col md:col-span-2">
                <label className="text-xs font-semibold text-gray-600 mb-1">
                  {isGlobalNoteType(currentTypeNormalized) ? "Note" : "Hover text"}
                </label>
                <input
                  className="border p-2 rounded"
                  value={form.notes}
                  onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional"
                />
              </div>

              {isAdmin && (
                <div className="flex flex-col md:col-span-2">
                  <label className="text-xs font-semibold text-gray-600 mb-1">Status</label>
                  <select
                    className="border p-2 rounded"
                    value={form.status}
                    onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                    disabled={isGlobalNoteType(currentTypeNormalized)}
                  >
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="denied">Denied</option>
                  </select>
                  {isGlobalNoteType(currentTypeNormalized) && (
                    <div className="text-xs text-gray-500 mt-1">Global notes are always approved.</div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-5 flex items-center justify-between">
              <div>
                {modalMode === "edit" && activeReq?.id && canEdit(activeReq) && (
                  <button
                    onClick={() => deleteRequest(activeReq)}
                    className="px-3 py-2 rounded border text-red-600 hover:bg-red-50"
                  >
                    Delete
                  </button>
                )}
              </div>

              <div className="flex gap-2 items-center">
                {!canSaveCurrent() && (
                  <div className="text-xs text-gray-500 mr-2">
                    Esther can delete this Full Day entry, but only admins can edit/save it.
                  </div>
                )}

                <button onClick={closeModal} className="px-4 py-2 rounded border bg-white hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  onClick={saveRequest}
                  disabled={!canSaveCurrent()}
                  className={[
                    "px-4 py-2 rounded text-white",
                    canSaveCurrent() ? "bg-[#1B80BA] hover:opacity-90" : "bg-[#1B80BA]/50 cursor-not-allowed",
                  ].join(" ")}
                >
                  Save
                </button>
              </div>
            </div>

            {!isAdmin && (
              <div className="mt-3 text-xs text-gray-500">
                Vacation, Sick and Flex Days auto-approve if fewer than 2 people are off; otherwise pending.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
